import { useState } from 'react';
import { toast } from 'react-toastify';
import { Bell, Mail, Wallet, Calendar, AlertCircle, Info } from 'lucide-react';

// Custom Toggle Component
const Toggle = ({ checked, onChange, disabled = false }) => (
    <button
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative w-12 h-6 rounded-full transition-all duration-200 ${checked ? 'bg-primary' : 'bg-neutral-300'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${checked ? 'translate-x-6' : 'translate-x-0'
            }`} />
    </button>
);

const notificationCategories = [
    {
        id: 'payments',
        label: 'Ödeme Bildirimleri',
        description: 'Ödeme alındı, hakediş ödemesi ve finansal işlemler',
        icon: Wallet,
        color: 'success',
    },
    {
        id: 'installments',
        label: 'Taksit Bildirimleri',
        description: 'Geciken taksitler ve vade hatırlatmaları',
        icon: Calendar,
        color: 'warning',
    },
    {
        id: 'system',
        label: 'Sistem Bildirimleri',
        description: 'Bakım, güncelleme ve sistem mesajları',
        icon: AlertCircle,
        color: 'info',
    },
];

const channels = [
    { id: 'portal', label: 'Portal', icon: Bell },
    { id: 'email', label: 'E-posta', icon: Mail },
];

export default function NotificationSettings() {
    const [settings, setSettings] = useState({
        payments: { portal: true, email: true },
        installments: { portal: true, email: true },
        system: { portal: true, email: false },
    });

    const handleToggle = (category, channel, value) => {
        setSettings(prev => ({
            ...prev,
            [category]: { ...prev[category], [channel]: value }
        }));
        toast.success('Bildirim tercihi güncellendi');
    };

    const colorClasses = {
        success: 'bg-success/10 text-success',
        warning: 'bg-warning/10 text-warning',
        info: 'bg-info/10 text-info',
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-bold text-text-primary">Bildirim Tercihleri</h2>
                <p className="text-text-tertiary text-sm mt-1">Hangi olaylarda nasıl haberdar olmak istediğinizi seçin</p>
            </div>

            {/* Info Banner */}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-info/5 border border-info/20">
                <Info size={20} className="text-info shrink-0 mt-0.5" />
                <div className="text-sm">
                    <p className="font-medium text-text-primary">Bildirim Kanalları</p>
                    <p className="text-text-tertiary mt-0.5">Portal bildirimleri uygulama içinde, e-posta bildirimleri kayıtlı adresinize gönderilir.</p>
                </div>
            </div>

            {/* Notification Categories */}
            <div className="space-y-4">
                {notificationCategories.map((category) => {
                    const Icon = category.icon;
                    return (
                        <div
                            key={category.id}
                            className="bg-bg-tertiary/30 rounded-xl border border-border-light overflow-hidden"
                        >
                            {/* Category Header */}
                            <div className="px-4 sm:px-5 py-4 border-b border-border-light flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${colorClasses[category.color]}`}>
                                    <Icon size={20} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-text-primary">{category.label}</h3>
                                    <p className="text-sm text-text-tertiary mt-0.5">{category.description}</p>
                                </div>
                            </div>

                            {/* Channel Toggles - Responsive */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 sm:p-5">
                                {channels.map((channel) => {
                                    const ChannelIcon = channel.icon;
                                    const isEnabled = settings[category.id][channel.id];
                                    return (
                                        <div
                                            key={channel.id}
                                            className={`flex items-center justify-between p-3 sm:p-4 rounded-xl border transition-colors ${isEnabled
                                                    ? 'bg-primary/5 border-primary/20'
                                                    : 'bg-bg-secondary border-border-light'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <ChannelIcon size={18} className={isEnabled ? 'text-primary' : 'text-text-muted'} />
                                                <span className={`text-sm font-medium ${isEnabled ? 'text-text-primary' : 'text-text-tertiary'}`}>
                                                    {channel.label}
                                                </span>
                                            </div>
                                            <Toggle
                                                checked={isEnabled}
                                                onChange={(val) => handleToggle(category.id, channel.id, val)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
